/**
 * Card Component
 *
 * A versatile card container with header, content, and footer sections.
 * Supports utility class variants (glass-card, dashboard-card, metric-card, performance-card).
 *
 * @example
 * ```tsx
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Title</CardTitle>
 *     <CardDescription>Description</CardDescription>
 *     <CardAction><Button>Action</Button></CardAction>
 *   </CardHeader>
 *   <CardContent>Content here</CardContent>
 *   <CardFooter>Footer content</CardFooter>
 * </Card>
 * ```
 */

import { cn } from '../utils';
import * as React from 'react';

// =============================================================================
// CARD ROOT
// =============================================================================

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual variant - applies predefined styles */
  variant?: 'default' | 'glass' | 'dashboard' | 'metric' | 'performance' | 'status-active';
  /** Whether the card is interactive (adds hover states) */
  interactive?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', interactive = false, ...props }, ref) => {
    // Check if custom utility class is provided
    const hasUtilityClass =
      className?.includes('glass-card') ||
      className?.includes('dashboard-card') ||
      className?.includes('metric-card') ||
      className?.includes('performance-card');

    // Map variant prop to utility class
    const variantClass = {
      default: '',
      glass: 'glass-card',
      dashboard: 'dashboard-card',
      metric: 'metric-card',
      performance: 'performance-card',
      'status-active': '',
    }[variant];

    return (
      <div
        ref={ref}
        data-slot="card"
        className={cn(
          // Base styles - Figma aligned: border-2, 16.4px radius, card shadow
          // Using shadow-card utility class (defined in tailwind.config.js and safelisted)
          !hasUtilityClass && !variantClass &&
            'border-2 border-(--card-border) rounded-(--card-border-radius) bg-card text-card-foreground shadow-card',
          // Variant class
          variantClass,
          // Status-active variant: green tint bg, border, left accent (Figma Frame 3 timer banner)
          variant === 'status-active' &&
            'rounded-(--card-border-radius) bg-(--status-card-active-bg) border border-(--status-card-active-border) border-l-4 border-l-(--status-card-active-accent) text-card-foreground shadow-card',
          // Interactive states
          interactive && 'transition-shadow hover:shadow-md cursor-pointer',
          className,
        )}
        {...props}
      />
    );
  },
);
Card.displayName = 'Card';

// =============================================================================
// CARD HEADER
// =============================================================================

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether to show border below header */
  bordered?: boolean;
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, bordered = false, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="card-header"
      className={cn(
        'flex flex-col space-y-1.5 p-(--space-card)',
        // Support for card-action grid layout
        'has-[*[data-slot=card-action]]:grid has-[*[data-slot=card-action]]:grid-cols-[1fr_auto] has-[*[data-slot=card-action]]:items-start',
        bordered && 'border-b pb-(--space-card)',
        className,
      )}
      {...props}
    />
  ),
);
CardHeader.displayName = 'CardHeader';

// =============================================================================
// CARD TITLE
// =============================================================================

export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Heading level for accessibility */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Component = 'h3', ...props }, ref) => (
    <Component
      ref={ref}
      data-slot="card-title"
      className={cn(
        // Use explicit type hints for Tailwind arbitrary values
        // text-[length:...] for font-size to avoid Tailwind guessing wrong
        'text-(length:--card-title-size) font-(--card-title-weight) leading-(--card-title-line-height) tracking-tight',
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

// =============================================================================
// CARD DESCRIPTION
// =============================================================================

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    data-slot="card-description"
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

// =============================================================================
// CARD ACTION (for header actions)
// =============================================================================

const CardAction = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-action"
    className={cn(
      'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
      className,
    )}
    {...props}
  />
));
CardAction.displayName = 'CardAction';

// =============================================================================
// CARD CONTENT
// =============================================================================

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-content"
    className={cn('p-(--space-card) pt-0', className)}
    {...props}
  />
));
CardContent.displayName = 'CardContent';

// =============================================================================
// CARD FOOTER
// =============================================================================

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether to show border above footer */
  bordered?: boolean;
}

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, bordered = false, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="card-footer"
      className={cn(
        'flex items-center p-(--space-card) pt-0',
        bordered && 'border-t pt-(--space-card)',
        className,
      )}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

// =============================================================================
// EXPORTS
// =============================================================================

export {
    Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle,
};

